// NPM Package Imports
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Formik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import Auth from 'j-toker';
import moment from 'moment';
import styled from 'styled-components';
import jstz from 'jstz';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// Helper Imports
import { isValidEmail } from '../../../helpers/Validator';
import { apiPost, apiPut, apiGet } from 'helpers/ApiHelper';
import { formatPhone } from 'helpers/Formatter';
import { deepDiver } from 'helpers/utilities';
import { getTranslatedString } from 'helpers/i18n';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import { PROSPECTS_STATUS } from 'helpers/StatusHelper';

// Action imports
import { getLandlordTrafficSources } from 'actions/landlordLeadsActions';

//UI imports
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import ReactPhoneInput from 'components/shared/ReactPhoneInput';

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;
const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

class CreateProspectDrawerDetailsStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: undefined,
      birthdayMode: 'year',
      unitTypes: [],
    };
  }

  componentDidMount() {
    const timezone = jstz.determine();

    this.setState({
      timezone: timezone.name() || 'America/Toronto',
    });

    if (deepDiver(this.props, 'formData.property_id')) {
      this.props.getLandlordTrafficSources(this.props.formData.property_id);
      this.fetchAvailableUnitTypes(this.props.formData.property_id);
    }
  }

  fetchAvailableUnitTypes = (propertyId) => {
    apiGet(`${Auth.getApiUrl()}/landlords/properties/${propertyId}/available_unit_types`)
      .done((response) => {
        this.setState({
          unitTypes: response.unit_types,
        });
      })
      .catch((error) => {});
  };

  handlePropertyChange = (value, setFieldValue, property) => {
    this.fetchAvailableUnitTypes(value);
    this.props.getLandlordTrafficSources(value);
    setFieldValue('property_id', value);
    setFieldValue('desired_unit', []);
    if (this.props.yardiEnabled) {
      setFieldValue('traffic_source', undefined, true);
    }
  };

  handleFormSubmit = (values) => {
    const payload = {
      ...values,
    };
    this.props.onLoadingChange({ isLoading: true, values: payload });
    this.handleAPIUrl(payload)
      .done((response) => {
        this.props.onSubmissionComplete(payload);
      })
      .catch((error) => {
        this.props.onLoadingChange({
          isError: true,
          isLoading: false,
          values: payload,
        });
      });
  };

  handleAPIUrl = (payload) => {
    if (this.props.isEditing) {
      return apiPut(Auth.getApiUrl() + `/landlords/lead_cards/${this.props.formData.id}`, payload);
    }

    return apiPost(Auth.getApiUrl() + '/landlords/prospects', payload);
  };

  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    const invalidEmailVerbiage = getTranslatedString('invalidEmail');
    const invalidPhoneVerbiage = getTranslatedString('validationErrors.invalidPhone');

    let validationSchema = {
      traffic_source: Yup.string().required(requiredErrorVerbiage),
      max_budget: Yup.string().required(requiredErrorVerbiage),
      first_name: Yup.string().required(requiredErrorVerbiage),
      last_name: Yup.string().required(requiredErrorVerbiage),
      email: Yup.string().test('email', invalidEmailVerbiage, function (value) {
        return isValidEmail(value);
      }),
      phone_number: Yup.string()
        .required(requiredErrorVerbiage)
        .test('phone_number', invalidPhoneVerbiage, function (value) {
          if (!value) return false;
          if (value.replace(/\s/g, '') === '+00000000000') return true;
          const parseResult = parsePhoneNumberFromString(value);
          return parseResult && parseResult.isValid();
        }),
      property_id: Yup.string().required(requiredErrorVerbiage),
      desired_unit: Yup.string().required(requiredErrorVerbiage),
      birthday: Yup.date(),
      move_in_date: Yup.date().required(requiredErrorVerbiage),
      budget_range: Yup.string().required(requiredErrorVerbiage),
    };

    if (!this.props.isEditing) {
      validationSchema = {
        ...validationSchema,
        notes: Yup.string().required('Required'),
      };
    }

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  handleBirthdayChange = (value, setFieldValue) => {
    this.setState({ birthdayMode: 'year' });
    setFieldValue('birthday', value, true);
  };

  render() {
    const { searchActivated, selectedItem } = this.state;
    const { formData } = this.props;

    let budget_range; // set initial value of budget_range to undefined when creating new prospect
    if (formData.min_budget && formData.max_budget) {
      // when editing an existing prospect
      if ([formData.min_budget, formData.max_budget].includes('$0.00')) {
        budget_range = getTranslatedString('landlordPortal.flexibleBudget');
      } else {
        budget_range = `${formData.min_budget} - ${formData.max_budget}`;
      }
    }

    return (
      <React.Fragment>
        <Formik
          initialValues={{
            property_id: formData.property_id,
            desired_unit: formData.desired_unit,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || undefined,
            phone_number: formatPhone(formData.phone_number, false),
            birthday: formData.birthday ? moment(formData.birthday) : undefined,
            move_in_date: formData.move_in_date ? moment(formData.move_in_date) : undefined,
            traffic_source: formData.traffic_source,
            max_budget: formData.max_budget,
            min_budget: formData.min_budget,
            budget_range,
            notes: formData.notes,
            last_contacted_at: formData.last_contacted_at
              ? moment(formData.last_contacted_at)
              : undefined,
          }}
          onSubmit={(values) => this.handleFormSubmit(values)}
          validate={this.validateForm}
        >
          {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => (
            <VerticalFormWrapper id={this.props.formName} onSubmit={handleSubmit}>
              {searchActivated &&
                !selectedItem &&
                this.renderSearchLead(errors, touched, setFieldValue)}
              {searchActivated && !!selectedItem && this.renderSelectedLead()}
              {!searchActivated &&
                this.renderCreateLead(values, errors, touched, handleChange, setFieldValue)}
              {this.renderAdditionalInformation(
                values,
                errors,
                touched,
                handleChange,
                setFieldValue
              )}
            </VerticalFormWrapper>
          )}
        </Formik>
      </React.Fragment>
    );
  }

  renderCreateNewProspectHeader = () => (
    <Spacer mb={1}>
      <FormTitle>{getTranslatedString('showingDrawer.createNewLead')}</FormTitle>
    </Spacer>
  );

  renderCreateLead = (values, errors, touched, handleChange, setFieldValue) => {
    const isConverted = [PROSPECTS_STATUS.CONVERTED, PROSPECTS_STATUS.CLOSED].includes(
      this.props.formData.status
    );

    return (
      <React.Fragment>
        {isConverted && (
          <React.Fragment>
            <p style={{ textAlign: 'center' }}>
              <i className="em em-warning" style={{ marginBottom: '5px' }} />
              <br />
              {getTranslatedString('showingDrawer.prospectConverted.warning')}
            </p>
            <div
              style={{
                textAlign: 'center',
                marginBottom: '16px',
                fontSize: 'large',
              }}
            >
              {this.props.formData.cotenant_id && (
                <Link
                  className="link-button"
                  to={`/companies/tenants?tenantId=${this.props.formData.cotenant_id}`}
                >
                  {getTranslatedString('showingDrawer.prospectConverted.view')}{' '}
                  {<TenantResidentVerbiageHelper />}
                </Link>
              )}
            </div>
          </React.Fragment>
        )}
        {!this.props.formData.id && this.renderCreateNewProspectHeader()}
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.firstName')}*`}
                validateStatus={touched.first_name && errors.first_name ? 'error' : null}
                help={touched.first_name && errors.first_name}
              >
                <FullWidthInput
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  placeholder="e.g. James"
                  size="large"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.lastName')}*`}
                validateStatus={touched.last_name && errors.last_name ? 'error' : null}
                help={touched.last_name && errors.last_name}
              >
                <FullWidthInput
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  placeholder="e.g. Ford"
                  size="large"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Email"
                validateStatus={touched.email && errors.email ? 'error' : null}
                help={touched.email && errors.email}
              >
                <FullWidthInput
                  name="email"
                  label={getTranslatedString('showingDrawer.email')}
                  value={values.email}
                  onChange={handleChange}
                  placeholder="e.g. james@ford.com"
                  size="large"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.phoneNumber')}*`}
                validateStatus={touched.phone_number && errors.phone_number ? 'error' : null}
                help={touched.phone_number && errors.phone_number}
              >
                <ReactPhoneInput
                  value={values.phone_number}
                  onChange={(value) => setFieldValue('phone_number', value)}
                  hasError={touched.phone_number && errors.phone_number}
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={getTranslatedString('showingDrawer.birthday')}
                validateStatus={touched.birthday && errors.birthday ? 'error' : null}
                help={touched.birthday && errors.birthday}
              >
                <FullWidthDatePicker
                  name="birthday"
                  defaultValue={values.birthday}
                  value={values.birthday}
                  onChange={(value) => this.handleBirthdayChange(value, setFieldValue)}
                  onPanelChange={(value, mode) => {
                    setFieldValue('birthday', undefined); // Have to reset birthday field otherwise, cannot select new month and new year
                    if (mode !== this.state.birthdayMode) {
                      this.setState({ birthdayMode: mode });
                    }
                  }}
                  disabledDate={(current) => current && current > moment().subtract(1, 'day')}
                  maxDate={moment().subtract(16, 'years')}
                  mobileMode="date"
                  mode={this.state.birthdayMode}
                  placeholder="e.g. 1990-01-01"
                  size="large"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
      </React.Fragment>
    );
  };

  renderAdditionalInformation = (values, errors, touched, handleChange, setFieldValue) => {
    const { properties } = this.props;
    const selectedProperty = properties.find((property) => property.value === values.property_id);
    const isConverted = [PROSPECTS_STATUS.CONVERTED, PROSPECTS_STATUS.CLOSED].includes(
      this.props.formData.status
    );

    let budgetRanges = [];
    if (!!selectedProperty) {
      budgetRanges = [...selectedProperty.rent_ranges];
    }
    budgetRanges.push(getTranslatedString('landlordPortal.flexibleBudget'));

    const handleBudgetRangeChange = (value) => {
      const budgetValues = value.split(' - ');
      if (budgetValues.length === 2) {
        setFieldValue('min_budget', budgetValues[0]);
        setFieldValue('max_budget', budgetValues[1]);
      } else if (budgetValues.length === 1 && budgetValues[0].includes('$')) {
        setFieldValue('min_budget', budgetValues[0]);
        setFieldValue('max_budget', budgetValues[0]);
      } else {
        setFieldValue('min_budget', '$0.00');
        setFieldValue('max_budget', '$0.00');
      }
      setFieldValue('budget_range', value, true);
    };

    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>{getTranslatedString('showingDrawer.additionalInformation')}</FormTitle>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              {this.renderPropertyField(values, setFieldValue, touched, errors, isConverted)}
            </Col>
            <Col span={12}>
              {this.renderUnitTypeField(values, setFieldValue, touched, errors, isConverted)}
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.moveInDate')}*`}
                validateStatus={touched.move_in_date && errors.move_in_date ? 'error' : null}
                help={touched.move_in_date && errors.move_in_date}
              >
                <FullWidthDatePicker
                  name="move_in_date"
                  defaultValue={values.move_in_date}
                  value={values.move_in_date}
                  onChange={(value) => setFieldValue('move_in_date', value, true)}
                  disabledDate={(current) =>
                    current && current < moment().subtract(1, 'day').endOf('day')
                  }
                  placeholder="e.g. 2020-01-01"
                  minDate={new Date()}
                  mobileMode="date"
                  size="large"
                  id="error"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.trafficSource')}*`}
                validateStatus={touched.traffic_source && errors.traffic_source ? 'error' : null}
                help={touched.traffic_source && errors.traffic_source}
              >
                <FullWidthSelect
                  name="traffic_source"
                  value={values.traffic_source}
                  onChange={(value) => setFieldValue('traffic_source', value, true)}
                  placeholder={getTranslatedString('showingDrawer.select')}
                  size="large"
                  disabled={(this.props.yardiEnabled && !selectedProperty) || isConverted}
                >
                  {this.props.trafficSources
                    .filter((source) => source.active || source.id === values.traffic_source)
                    .map((source) => (
                      <Select.Option value={source.id}>{source.traffic_source}</Select.Option>
                    ))}
                </FullWidthSelect>
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={`${getTranslatedString('landlordPortal.budgetRange')}*`}
                validateStatus={touched.budget_range && errors.budget_range ? 'error' : null}
                help={touched.budget_range && errors.budget_range}
              >
                <FullWidthSelect
                  name="budget_range"
                  value={values.budget_range}
                  onChange={handleBudgetRangeChange}
                  placeholder={getTranslatedString('showingDrawer.select')}
                  size="large"
                  disabled={!selectedProperty || isConverted}
                >
                  {budgetRanges.map((option) => (
                    <Select.Option value={option}>{option}</Select.Option>
                  ))}
                </FullWidthSelect>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={getTranslatedString('showingDrawer.lastContactedDate')}>
                <FullWidthDatePicker
                  name="last_contacted_date"
                  defaultValue={values.last_contacted_at}
                  value={values.last_contacted_at}
                  onChange={(value) => setFieldValue('last_contacted_at', value, true)}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  placeholder="e.g. 2020-01-01"
                  minDate={new Date()}
                  mobileMode="date"
                  size="large"
                  id="error"
                  disabled={isConverted}
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        {!this.props.isEditing &&
          this.renderCreateNewProspectNotes(touched, errors, values, handleChange)}
      </React.Fragment>
    );
  };

  renderCreateNewProspectNotes = (touched, errors, values, handleChange) => (
    <Spacer mb={1}>
      <Form.Item
        label={`${getTranslatedString('showingDrawer.notes')}*`}
        validateStatus={touched.notes && errors.notes ? 'error' : null}
        help={touched.notes && errors.notes}
      >
        <FullWidthTextArea
          placeholder={getTranslatedString('showingDrawer.addInternalNotes')}
          size="large"
          name="notes"
          value={values.notes}
          onChange={handleChange}
        />
      </Form.Item>
    </Spacer>
  );

  renderUnitTypeField = (values, setFieldValue, touched, errors, isConverted) => {
    // Load only unit types that are either available within property,
    // Or already selected for this prospect
    // Filter all other unit types out
    const availableUnitTypeIds = this.state.unitTypes.map((unitType) => unitType.value);
    const allUnitTypes = this.props.unitTypes;
    const unitTypes = allUnitTypes.filter((unitType) => {
      const isAvailableInProperty = availableUnitTypeIds.includes(unitType.value);
      const isSelected =
        Array.isArray(values.desired_unit) && values.desired_unit.includes(unitType.value);
      return isAvailableInProperty || isSelected;
    });
    return (
      <Form.Item
        label={`${getTranslatedString('showingDrawer.selectAUnitType')}*`}
        validateStatus={touched.desired_unit && errors.desired_unit ? 'error' : null}
        help={touched.desired_unit && errors.desired_unit}
      >
        <FullWidthSelect
          mode="multiple"
          name="desired_unit"
          showSearch
          placeholder="Select"
          size="large"
          value={values.desired_unit}
          onChange={(value) => setFieldValue('desired_unit', value)}
          filterOption={(input, option) => {
            const desiredUnitOption = deepDiver(option, 'props.children.1');
            if (!desiredUnitOption) return;

            return desiredUnitOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          disabled={unitTypes.length === 0 || isConverted}
        >
          {unitTypes.map((unitType) => (
            <Select.Option key={`unit_type_${unitType.value}`} value={unitType.value}>
              {!unitType.translation_key && unitType.label}
              {unitType.translation_key &&
                getTranslatedString(`standardUnitTypes.${unitType.translation_key}`)}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderPropertyField = (values, setFieldValue, touched, errors, isConverted) => {
    const { properties } = this.props;

    return (
      <Form.Item
        label={`${getTranslatedString('showingDrawer.selectAProperty')}*`}
        validateStatus={touched.property_id && errors.property_id ? 'error' : null}
        help={touched.property_id && errors.property_id}
      >
        <FullWidthSelect
          name="property_id"
          showSearch
          placeholder="Select"
          size="large"
          value={values.property_id}
          onChange={(value) => this.handlePropertyChange(value, setFieldValue)}
          filterOption={(input, option) => {
            const desiredPropertyOption = deepDiver(option, 'props.children');
            if (!desiredPropertyOption) return;

            return desiredPropertyOption.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          disabled={isConverted}
        >
          {properties.map((property) => (
            <Select.Option value={property.value} key={property.value}>
              {property.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };
}

export default connect(
  (store) => ({
    properties: store.landlordProperties.properties,
    yardiEnabled: store.userTypes.yardiEnabled,
  }),
  {
    getLandlordTrafficSources,
  }
)(CreateProspectDrawerDetailsStep);
